import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CompactTransaction,
  CurrentProfit,
  SingleProfitCalculationApiResponse,
  Transaction,
} from '../../../types';

import Charts from './Charts';
import Transactions from './Transactions';
import {
  findTransactionWave,
  transformToHeikinCandles,
} from '../../../../../helpers/bundleTransactionsToCompactForm';
import {
  Candle,
  ComplexParamsBundle,
  LatestBotState,
  TimeFrame,
} from '../../../../Detail/types';
import ProfitInformation from './ProfitInformation';
import { ComponentDimensions } from '../../../../../types/Layout';

interface Props {
  data: SingleProfitCalculationApiResponse | undefined;
}

const SingleProfitCalculationResult = (props: Props) => {
  const { data } = props;

  const [rootDimensions, setRootDimensions] = useState<
    ComponentDimensions | undefined
  >(undefined);
  const rootRef = useRef<any>(null);

  useEffect(() => {
    if (!rootRef.current) {
      return;
    }

    if (!!rootDimensions) {
      return;
    }

    setRootDimensions({
      height: rootRef.current.clientHeight * 0.9,
      width: rootRef.current.clientWidth,
    });
  }, [rootDimensions]);

  const [isChartAnimationDone, setIsChartAnimationDone] = useState(false);

  const [domainX, setDomainX] = useState<[number, number] | undefined>();

  const compactTransactions = useMemo(() => {
    if (!data) {
      return [];
    }

    const compacts = Object.assign(
      [] as Transaction[],
      data.transactions.map((t) => ({ ...t })),
    );

    compacts.forEach((compactTransaction, index) => {
      // Add signals from profit calcs response
      // Filter between this transaction timestamp and next compact transaction startTimestamp
      const signals = data?.strongestSignals;

      if (!signals?.length) {
        return;
      }

      const signalsEndTimestamp =
        index !== compacts.length - 1
          ? compacts[index + 1].timestamp
          : new Date().valueOf();

      compactTransaction.signals = signals?.filter(
        (s) =>
          compactTransaction.timestamp <= s.timestamp &&
          s.timestamp <= signalsEndTimestamp,
      );
    });

    return compacts;
  }, [data]);

  const shorterTHeikinCandles = useMemo(() => {
    if (!data?.savedData?.shorterTNormalCandles) {
      return [];
    }

    const shorterTNormalCandles = data?.savedData?.shorterTNormalCandles.reduce(
      (acc, curr) => {
        acc.push(...curr);
        return acc;
      },
      [],
    );

    if (!shorterTNormalCandles) {
      return [];
    }

    const shorterTHeikinCandles = transformToHeikinCandles(
      shorterTNormalCandles,
    );

    return shorterTHeikinCandles;
  }, [data?.savedData?.shorterTNormalCandles]);

  const multiTHeikinCandles = useMemo(() => {
    if (!data?.savedData?.multiOrderCandles) {
      return [];
    }

    const multiOrderHeikinCandles = data?.savedData?.multiOrderCandles.reduce<
      Candle[]
    >((acc, curr) => {
      const t = curr.reduce((acc2, curr2) => {
        acc2.push(...curr2);
        return acc2;
      }, []);

      acc.push(...[...t]);
      return acc;
    }, []);

    if (!multiOrderHeikinCandles) {
      return [];
    }

    const multiTHeikinCandles = transformToHeikinCandles(
      multiOrderHeikinCandles,
    );

    return multiTHeikinCandles;
  }, [data?.savedData?.multiOrderCandles]);

  const setCandles = useCallback(
    (transaction: Transaction | CompactTransaction) => {
      if (!data) {
        return;
      }

      const higherTHeikinCandles = [
        ...data.savedData.higherTHeikinCandles,
      ].sort((a, b) => a.startTimestamp - b.startTimestamp);

      /**
       * De trade in het midden
       * Links en rechts de vorige en volgende trade (compact)
       */

      let startTimestamp, endTimestamp;
      const compactTransactionId = transaction.compactTransactionId;

      const compactTransactionIndex = compactTransactions.findIndex(
        (t) => t.compactTransactionId === compactTransactionId,
      );

      if (compactTransactionIndex === 0) {
        startTimestamp = domainX?.[0] || transaction.timestamp;
      } else {
        const previousCompactTransaction =
          compactTransactions[compactTransactionIndex - 1];
        startTimestamp = previousCompactTransaction.timestamp;
      }

      if (compactTransactionIndex === compactTransactions.length - 1) {
        endTimestamp = new Date().valueOf();
      } else {
        const nextCompactTransaction =
          compactTransactions[compactTransactionIndex + 1];

        endTimestamp = nextCompactTransaction.timestamp;
      }

      const higherTransactionWave = findTransactionWave(
        higherTHeikinCandles,
        startTimestamp,
        endTimestamp,
      );

      if (!higherTransactionWave?.length) {
        return;
      }

      const start = higherTransactionWave[0].startTimestamp;
      const end =
        higherTransactionWave[higherTransactionWave.length - 1].endTimestamp;

      transaction.candles = {
        higher: higherTransactionWave,
        shorter: shorterTHeikinCandles.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
        ),
        multi: multiTHeikinCandles.filter(
          (c) => start <= c.startTimestamp && c.endTimestamp <= end,
          // waveStartTimstamp <= c.startTimestamp &&
          // c.startTimestamp <=
          //   transaction.timestamp + 5 * (c.endTimestamp - c.startTimestamp),
        ),
      };
    },
    [
      compactTransactions,
      data,
      domainX,
      multiTHeikinCandles,
      shorterTHeikinCandles,
    ],
  );

  const transactions = useMemo(() => {
    if (!data) {
      return [];
    }

    const ts = Object.assign(
      [] as Transaction[],
      data.transactions.map((t) => ({ ...t })),
    );
    ts.sort((a, b) => a.timestamp - b.timestamp);

    const filteredTransactions = ts.filter(
      (t) =>
        !domainX || (t.timestamp >= domainX[0] && t.timestamp <= domainX[1]),
    );

    if (
      !data?.savedData?.higherTHeikinCandles ||
      compactTransactions.length === 0
    ) {
      return filteredTransactions;
    }

    const higherTHeikinCandles = [...data.savedData.higherTHeikinCandles].sort(
      (a, b) => a.startTimestamp - b.startTimestamp,
    );

    for (let i = 0; i < filteredTransactions.length; i++) {
      const transaction = filteredTransactions[i];

      if (transaction.transactionType.includes('TAKE_PROFIT')) {
        // Set candles from previous compact transaction to the next one
        const transactionTimestamp = transaction.timestamp;
        const closestCompactTransactionIndex = parseInt(
          Object.entries(
            compactTransactions.map((ct) =>
              Math.abs(transactionTimestamp - ct.timestamp),
            ),
          ).sort((a, b) => a[1] - b[1])[0][0],
        );
        const closestCompactTransaction =
          compactTransactions[closestCompactTransactionIndex];

        let start: number, end: number;

        if (closestCompactTransaction.timestamp > transactionTimestamp) {
          start =
            closestCompactTransactionIndex > 0
              ? compactTransactions[closestCompactTransactionIndex - 1]
                  .timestamp
              : higherTHeikinCandles[0].startTimestamp;
          end = closestCompactTransaction.timestamp;
        } else {
          start = closestCompactTransaction.timestamp;
          end =
            closestCompactTransactionIndex === compactTransactions.length - 1
              ? new Date().valueOf()
              : compactTransactions[closestCompactTransactionIndex + 1]
                  .timestamp;
        }

        const higherTCandles = higherTHeikinCandles.filter(
          (c) =>
            start - (c.endTimestamp - c.startTimestamp) <= c.startTimestamp &&
            c.endTimestamp <= end + (c.endTimestamp - c.startTimestamp),
        );

        transaction.candles = {
          higher: higherTCandles,
          shorter: shorterTHeikinCandles.filter(
            (c) =>
              higherTCandles[0].startTimestamp <= c.startTimestamp &&
              c.endTimestamp <=
                higherTCandles[higherTCandles.length - 1].endTimestamp,
          ),
          multi: multiTHeikinCandles.filter(
            (c) =>
              higherTCandles[0].startTimestamp <= c.startTimestamp &&
              c.endTimestamp <=
                higherTCandles[higherTCandles.length - 1].endTimestamp,
            // waveStartTimstamp <= c.startTimestamp &&
            // c.startTimestamp <=
            //   transaction.timestamp + 5 * (c.endTimestamp - c.startTimestamp),
          ),
        };

        continue;
      }

      /**
       * De trade in het midden
       * Links en rechts de vorige en volgende trade (compact)
       */
      setCandles(transaction);

      const compactTransactionId = transaction.compactTransactionId;

      const compactTransactionIndex = compactTransactions.findIndex(
        (t) => t.compactTransactionId === compactTransactionId,
      );

      // Add signals from profit calcs response
      // Filter between this transaction timestamp and next compact transaction startTimestamp
      const signals = data.strongestSignals;
      const signalsEndTimestamp =
        compactTransactionIndex !== compactTransactions.length - 1
          ? compactTransactions[compactTransactionIndex + 1].timestamp
          : new Date().valueOf();
      transaction.signals = signals?.filter(
        (s) =>
          transaction.timestamp <= s.timestamp &&
          s.timestamp <= signalsEndTimestamp,
      );
    }

    return filteredTransactions;
  }, [
    compactTransactions,
    data,
    domainX,
    multiTHeikinCandles,
    setCandles,
    shorterTHeikinCandles,
  ]);

  useEffect(() => {
    compactTransactions.forEach((compactTransaction) => {
      setCandles(compactTransaction);
    });
  }, [compactTransactions, setCandles]);

  const currentComplexParams: ComplexParamsBundle | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return {
      complexParams: {
        cbrt: data.cbrt,
        shorterTimeFrameInterval: data.shorterTimeFrameInterval,
        supportCbrtRange: data.supportCbrtRange,
        supportMrcState: data.supportMrcState,
      },
      timeFrames: {
        higher: data.higherTimeFrame as TimeFrame,
        shorter: data.shorterTimeFrame as TimeFrame,
        multiOrder: data.multiOrderTimeFrame as TimeFrame,
      },
    };
  }, [data]);

  const currentProfit: CurrentProfit | undefined = useMemo(() => {
    if (!data) {
      return undefined;
    }

    return {
      currentProfit: data.currentProfit,
      currentPriceChange: data.currentPriceChange,
    };
  }, [data]);

  const latestBotState: LatestBotState | undefined = useMemo(() => {
    if (!data || !transactions.length) {
      return undefined;
    }

    /** We want to filter the multiT candles
     * start  of last wave (the opposite than latestOrderType) until now
     */

    const nonTPTransactions = transactions.filter(
      (t) => !t.transactionType.includes('TAKE_PROFIT'),
    );
    const latestTransaction = nonTPTransactions[nonTPTransactions.length - 1];
    const startTimestamp = latestTransaction.timestamp;

    if (!startTimestamp) {
      return undefined;
    }

    // @TODO ????
    let startOfWaveTimestamp = null;

    const candles = [...data.savedData.higherTHeikinCandles].sort(
      (a, b) => a.startTimestamp - b.startTimestamp,
    );

    for (let index = candles.length - 1; index >= 0; index--) {
      const candle = candles[index];
      const candleOrderType = candle.direction === 'up' ? 'BUY' : 'SELL';

      if (!candle.startOfNewTrend) {
        continue;
      }

      if (candleOrderType === latestTransaction?.orderType) {
        continue;
      }

      if (candle.startTimestamp < latestTransaction.timestamp) {
        break;
      }

      startOfWaveTimestamp = candle.startTimestamp;
      break;
    }

    if (!startOfWaveTimestamp) {
      startOfWaveTimestamp = latestTransaction.timestamp; // latest transaction timestamp
    }

    /** We want to filter all VISIBLE states,
     * even if the start is before the start of the wave
     * if the line is still visible, include it
     */

    const candleData =
      data.currentMrcState?.timeFrame === data.multiOrderTimeFrame
        ? multiTHeikinCandles
        : shorterTHeikinCandles;

    const wakeUpTimes =
      data.currentMrcState?.timeFrame === data.multiOrderTimeFrame
        ? data.wakeupTimes.multiT
        : data.wakeupTimes.shorterT;

    const indicatorConfig = !!data.indicatorConfig
      ? data.currentMrcState?.timeFrame === data.multiOrderTimeFrame
        ? data.indicatorConfig.multiT
        : data.indicatorConfig.shorterT
      : undefined;

    return {
      orderType: latestTransaction.orderType === 'BUY' ? 'SELL' : 'BUY',
      currentProfit: data.currentProfit,
      currentPriceChange: data.currentPriceChange,
      candles: candleData.filter(
        (c) => latestTransaction.timestamp <= c.startTimestamp,
      ),
      wakeUpTimes,
      cbrtRangeStates: data.cbrtRangeStates.filter(
        (s) => startTimestamp <= (s.endTimestamp || new Date().valueOf()),
      ),
      mrcStates: data.mrcStates.filter(
        (s) => startTimestamp <= (s.endTimestamp || new Date().valueOf()),
      ),
      transactions: data.transactions,
      baseAsset: data.baseAsset,
      quoteAsset: data.quoteAsset,
      timeFrame: (data.currentMrcState?.timeFrame ||
        data.multiOrderTimeFrame) as TimeFrame,
      exchange: data.exchange,
      signals: data.strongestSignals?.filter(
        (s) => startTimestamp <= s.timestamp,
      ),
      indicatorConfig,
    };
  }, [data, multiTHeikinCandles, shorterTHeikinCandles, transactions]);

  if (!data) {
    return null;
  }

  return (
    <div
      ref={rootRef}
      style={{
        flex: 1,
        height: '100%',
        opacity: rootDimensions ? 1 : 0,
        transition: 'opacity 500ms ease-in-out',
      }}
    >
      {!!rootDimensions && (
        <>
          <Charts
            data={data}
            setDomainX={setDomainX}
            allCompactTransactions={compactTransactions}
            rootDimensions={rootDimensions}
            setIsChartAnimationDone={setIsChartAnimationDone}
          />
          {/* <ProfitCalculationsResultCandleChart
        candles={data.savedData.higherTHeikinCandles}
        compactTransactions={compactTransactions}
        signals={data.strongestSignals}
      /> */}
          {isChartAnimationDone && (
            <>
              <div
                style={{
                  border: '1px solid rgba(50, 50, 50, 1)',
                  borderRadius: 8,
                  padding: 32,
                  marginTop: 96,
                  marginBottom: 16,
                  paddingTop: 64,
                  paddingBottom: 64,
                  flex: 1,
                  backgroundColor: 'rgba(30, 30, 30, 1)',
                  boxShadow: '4px 0px 5px rgba(255, 255, 255, 0.2)',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  width: '65%',
                }}
              >
                <ProfitInformation data={data} />
              </div>
              <Transactions
                transactions={transactions}
                baseAsset={data.baseAsset}
                quoteAsset={data.quoteAsset}
                compactTransactionLength={compactTransactions[0]?.id}
                allCompactTransactions={compactTransactions}
                currentComplexParams={currentComplexParams}
                currentProfit={currentProfit}
                cbrtRangeStates={data.cbrtRangeStates}
                mrcStates={data.mrcStates}
                latestBotState={latestBotState}
                wakeupTimes={data.wakeupTimes}
                indicatorConfig={data.indicatorConfig}
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

export default SingleProfitCalculationResult;
